
import { BodyScanGender, Gender,
    HandledError, IBodyScanAnalysisData, IBodyScanData,
    IBodyScanHistory, ITargetInfo, IWeightLog, MassUnit, MetricToImperialConverter } from 'fitbeat.models';
import moment from 'moment';
import { UserInfoForBodyScan } from '../../entities/bodyscan/userinfoforbodyscan';
import { BodyScanSerializer, IBodyScansRepository, IUserProfileRepository } from '../../persistence';
import { calcMuscleMassMeter, calcMuscleMassMeterKg, round } from '../analytics/StatHelpers';
import { IBodyScansManager } from './ibodyscansmanager';
export class BodyScansManager implements IBodyScansManager {

    public static percentageDifference(analysisData: IBodyScanAnalysisData): string {
        let limitRange = analysisData.upperLimitValue;
        if (analysisData.currentValue < analysisData.lowerLimitValue) {
            limitRange = analysisData.lowerLimitValue;
        }
        const percentage = (((analysisData.currentValue - limitRange) / (limitRange)) * 100);
        return percentage.toFixed(2);
    }

    constructor(
        private bodyScanRepository: IBodyScansRepository,
        private userProfileRepository: IUserProfileRepository) { }

    public getBodyScans(email: string): Promise<IBodyScanData[] | undefined> {
        return this.bodyScanRepository.get(email);
    }

    public async getBodyScanHistory(uid: string): Promise<IBodyScanHistory[] | undefined> {
        const userProfile = await this.userProfileRepository.get(uid);
        if (!userProfile) { return; }
        const scans = await this.bodyScanRepository.getByUid(uid);
        if (!scans) { return; }
        const history = this.useLatestScanPerDay(scans);
        let bodyScanHistory = history.map((scan) => {
            const weight = scan.analysis &&
                scan.analysis.weight ? scan.analysis.weight.currentValue : 0;
            const bodyFat = scan.analysis &&
                scan.analysis.percentBodyFat ? scan.analysis.percentBodyFat.currentValue : 0;
            const muscleMassMeter = calcMuscleMassMeter(userProfile, weight, bodyFat);
            const muscleMassMeterValue = round(calcMuscleMassMeterKg(userProfile, muscleMassMeter));
            const unit = MassUnit.Killogram;
            return {
                ...scan,
                weight,
                bodyFat,
                muscleMassMeter,
                muscleMassMeterValue,
                unit,
            };
        });
        if (userProfile && userProfile.useImperialUnit) {
            bodyScanHistory =  MetricToImperialConverter.convertBodyScanHistoryToImperial(bodyScanHistory);
        }
        return bodyScanHistory;
    }

    public async getBodyInfoForScan(email: string, scanDate: Date)
        : Promise<UserInfoForBodyScan> {
        const matchingSearch = await this.userProfileRepository.search(email);
        const matchingUser = matchingSearch.find((u) => u.email === email);

        if (matchingUser == null) {
            throw new HandledError(`Unable to scan. Not a recognised member email`);
        }

        if (matchingUser.height == null || matchingUser.dateOfBirth == null || matchingUser.gender == null) {
            throw new HandledError(
        `Unable to scan. Please capture your height, gender and age in your Fitbeat app before doing your scan`);
        }

        const validScanForThisMonth = await this.getValidScanForMonth(email, scanDate);
        if (validScanForThisMonth) {
            throw new HandledError(
                `No remaining scan credit for this month. Your credit recharges at the beginning of next month`);
        }

        return {
            genderNumber:
                matchingUser.gender === Gender.Male
                ? BodyScanGender.Male
                : BodyScanGender.Female,
            age: matchingUser.age,
            height: matchingUser.height,
        };
    }

    public async markLatestScansForThisMonthAsInvalid(email: string, scanDate: Date) {
        const validScanForThisMonth = await this.getValidScanForMonth(email, scanDate);
        if (!validScanForThisMonth) {
            return;
        }
        validScanForThisMonth.invalid = true;
        await this.bodyScanRepository.update(validScanForThisMonth);
    }

    public async addLegacyBodyScan(body: any, email: string) {
        const user = await this.userProfileRepository.getUserByEmail(email);
        if (!user) {
            return;
        }
        let bodyScan = {
            ...body,
            id: body.ID,
            uid: user.uid,
        };

        try {
            const bodyScanData = BodyScanSerializer.buildAnalyticData(bodyScan);
            const weight = bodyScanData.analysis &&
                bodyScanData.analysis.weight ? bodyScanData.analysis.weight.currentValue : 0;
            const bodyFat = bodyScanData.analysis &&
                bodyScanData.analysis.percentBodyFat ? bodyScanData.analysis.percentBodyFat.currentValue : 0;
            const calculatedMuscleMassMeter = calcMuscleMassMeter(user, weight, bodyFat);
            const calculatedMuscleMassMeterKg = round(calcMuscleMassMeterKg(user, calculatedMuscleMassMeter));

            bodyScan = {
                ...bodyScan,
                calculatedBodyFat: bodyFat,
                calculatedMuscleMassMeter,
                calculatedMuscleMassMeterKg,
            };

        } catch (error) {
            throw new HandledError(`Unable to calculate muscle mass based on body scan.`);
        }

        return await this.bodyScanRepository.add(bodyScan);
    }

    public async userWeightChange(
        uid: string, weight: number,
        requestTime: Date | undefined, timezone: string | undefined) {
        let testTime = moment().format('YYYY-MM-DD HH:mm:ss');
        if (requestTime && timezone) {
            testTime = moment(requestTime).tz(timezone).format('YYYY-MM-DD HH:mm:ss');
        }
        const manualWeightLog: IWeightLog = {
            uid,
            weight,
            testTime,
            analysis: {
                items: [
                    {
                        itemID: 'WT',
                        value: new Array(4).fill(weight.toString()),
                    },
                    {
                        itemID: 'SMM',
                        value: new Array(4).fill('0'),
                    },
                    {
                        itemID: 'PBF',
                        value: new Array(4).fill('0'),
                    },
                ],
            },
            isManualWeightLog: true,
        };
        return await this.bodyScanRepository.addManualWeightLog(manualWeightLog);
    }

    public async addTargetAndMilstone(
        uid: string,
        targetInfo: ITargetInfo | undefined, milestoneInfo: ITargetInfo | undefined,
        requestTime: Date, timezone: string) {
        const latestScan = await this.getLatestBodyscan(uid, requestTime, timezone);
        return await this.bodyScanRepository.updateScanWithTargetAndMilestone(latestScan.id, targetInfo, milestoneInfo);
    }

    private async getLatestBodyscan(uid: string, requestTime: Date, timezone: string) {
        const toDate = moment(requestTime).tz(timezone).endOf('day');
        const fromDate = toDate.clone().subtract(6, 'months').endOf('day');
        const latestBodyscanData =
            await this.bodyScanRepository.getByUidAndDateRange(uid, fromDate.toDate(), toDate.toDate(), 1);
        if (latestBodyscanData && latestBodyscanData.length > 0) {
            return latestBodyscanData[0];
        } else {
            throw new HandledError(`No body scan found for user.`);
        }
    }

    private doMonthsMatch(s: IBodyScanData, scanDate: Date): boolean {
        if (s.dateOfScanDateOnly == null) {
            return false;
        }
        return s.dateOfScanDateOnly.getFullYear() === scanDate.getFullYear()
            && s.dateOfScanDateOnly.getMonth() === scanDate.getMonth();
    }

    private async getValidScanForMonth(email: string, scanDate: Date): Promise<IBodyScanData | undefined> {
        const previousScans = await this.getBodyScans(email) || [];
        const validScanForThisMonth = previousScans.find((s) =>
            this.doMonthsMatch(s, scanDate) &&
            !s.invalid);
        return validScanForThisMonth;
    }

    private useLatestScanPerDay(scans: IBodyScanData[]) {
        const groupedScansByDay = scans.reduce((accumulator, item: any) => {
            const daySlice = item.dateOfScan.slice(0, 10);
            accumulator[daySlice] = accumulator[daySlice] || [];
            accumulator[daySlice].push(item);
            return accumulator;
        }, Object.create(null));
        const history = [];
        for (const key in groupedScansByDay) {
            if (Object.prototype.hasOwnProperty.call(groupedScansByDay, key)) {
                history.push(groupedScansByDay[key].slice(-1)[0]);
            }
        }
        return history;
    }
}
